'use client';
import { type FC } from 'react';

import { LicenseInfo } from '@mui/x-license';

let LastAppliedPremiumKey: string | undefined;

/**
 * Registers the MUI X premium license key with the global {@link LicenseInfo} registry, skipping
 * the call when the same key has already been applied.
 *
 * This runs during {@link MuiPremiumLicense}'s render rather than in an effect so the key is
 * registered before the premium components render in the same pass, on both the server and the
 * client. That keeps the server-rendered and first client-rendered output licensed and identical,
 * avoiding the unlicensed-watermark flash and hydration mismatch a client-only effect would
 * introduce. A missing key is a valid state, because it is only injected on authenticated pages, so
 * it is a no-op.
 */
const applyPremiumKey = (premiumKey: string | undefined): void => {
  if (premiumKey && LastAppliedPremiumKey !== premiumKey) {
    LicenseInfo.setLicenseKey(premiumKey);
    LastAppliedPremiumKey = premiumKey;
  }
};

export type MuiPremiumLicenseProps = {
  readonly premiumKey: string;
};

/**
 * Registers the MUI X premium license key with the global {@link LicenseInfo} registry through
 * {@link applyPremiumKey}, and renders nothing.
 *
 * Important!
 * ----------
 * This component must NEVER be dynamically imported, either with or without 'ssr: false'.  It
 * must be imported statically (server-rendered) and rendered before the premium components it
 * licenses:
 *
 * ```tsx
 * <MuiPremiumLicense premiumKey={auth.muiPremiumKey} />
 * <RestOfTheFrontendApplication />
 * ```
 *
 * (Note that before literally means the JSX render must be defined on the line before).
 *
 * Registering the license is a render-time side effect that has to run before those components
 * read it, on both the server and the first client render, so both stay licensed and identical.
 * A 'dynamic'/'ssr: false' render runs only on the client and only after its chunk loads, after
 * the premium components have already rendered unlicensed, while the server renders unlicensed too,
 * producing the watermark on one side and a hydration mismatch/error on the other.
 *
 * Registering globally is sufficient because the application resolves a single '@mui/x-license'
 * instance, which the emotion transpile in 'next.config.mts' guarantees.
 *
 * @see {@link https://mui.com/x/react/license/}
 */
export const MuiPremiumLicense: FC<MuiPremiumLicenseProps> = ({ premiumKey }) => {
  applyPremiumKey(premiumKey);
  return null;
};
